import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  cancelSubscription,
  clearErrors,
  createCheckoutSession,
  fetchSubscriptions,
  fetchUserSubscription,
  selectCreateSessionError,
  selectFetchSubscriptionsError,
  selectIsCancelingSubscription,
  selectIsCreatingCheckout,
  selectIsLoading as selectIsLoadingSubscriptions,
  selectIsLoadingUserSubscription,
  selectSubscriptions,
  selectUserSubscription,
  selectUserSubscriptionError
} from "../../redux/upgrade/subscriptionSlice";
import {SubscriptionPriceModel} from "../../models/subscriptionPrice";
import {BaseError} from "../../errors/baseError";
import {selectLoginStatus} from "../../redux/auth/authSlice";
import {AlertDialog} from "../dialogs/AlertDialog";
import {Heading2, Paragraph} from "../text";
import Skeleton from "react-loading-skeleton";
import {AccountSubscriptionOptionView} from "./AccountSubscriptionOptionView";
import {Button} from "../buttons/Button";
import {useRouter} from "next/router";

export const AccountSubscriptionSectionView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()

  const subscriptions = useAppSelector(selectSubscriptions)
  const isLoadingSubscriptions = useAppSelector(selectIsLoadingSubscriptions)
  const isLoadingUserSubscription = useAppSelector(selectIsLoadingUserSubscription)
  const isCreatingCheckout = useAppSelector(selectIsCreatingCheckout)
  const isCancelingSubscription = useAppSelector(selectIsCancelingSubscription)
  const createCheckoutError = useAppSelector(selectCreateSessionError)
  const cancelSubscriptionsError = useAppSelector(selectCreateSessionError)
  const userSubscriptionError = useAppSelector(selectUserSubscriptionError)
  const userSubscription = useAppSelector(selectUserSubscription)
  const fetchSubscriptionsError = useAppSelector(selectFetchSubscriptionsError)
  const loginStatus = useAppSelector(selectLoginStatus)

  const [error, setError] = useState<BaseError<any> | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(isLoadingSubscriptions || isLoadingUserSubscription)
  const [confirmCancelSubscription, setConfirmCancelSubscription] = useState<boolean>(false)
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionPriceModel | undefined>(subscriptions[0])

  const subtitleText = userSubscription?.status === 'active'
    ? t('subscription:subscription_subtitle_you_are_subscribed')
    : t('subscription:subscription_subtitle_you_are_not_subscribed')

  useEffect(() => {
    if (loginStatus === 'logged_in' || loginStatus === 'logged_in_anon') {
      dispatch(fetchUserSubscription())
      dispatch(fetchSubscriptions())
    }
  }, [loginStatus, dispatch])

  useEffect(() => {
    setIsLoading(isLoadingUserSubscription || isLoadingSubscriptions)
  }, [isLoadingSubscriptions, isLoadingUserSubscription])

  useEffect(() => {
    setSelectedSubscription(subscriptions[0])
  }, [subscriptions])

  useEffect(() => {
    if (createCheckoutError) {
      setError(createCheckoutError)
    } else if (cancelSubscriptionsError) {
      setError(cancelSubscriptionsError)
    } else if (userSubscriptionError) {
      setError(userSubscriptionError)
    } else if (fetchSubscriptionsError) {
      setError(fetchSubscriptionsError)
    }
  }, [createCheckoutError, cancelSubscriptionsError, userSubscriptionError, fetchSubscriptionsError])

  return <div className="w-full md:rounded-xl md:border-2 md:border-fg-gray-surface md:p-8 mx-auto">
    <AlertDialog
      isOpen={!!error}
      error={error}
      onClose={() => dispatch(clearErrors())}
      buttons={[
        {
          title: { key: 'common:common_title_ok' },
          onClick: () => {
            dispatch(clearErrors())
          },
          style: 'default'
        }
      ]}
    />

    <AlertDialog
      isOpen={confirmCancelSubscription}
      title={t('subscription:subscription_title_cancel_subscription')}
      message={t('subscription:subscription_subtitle_confirm_cancel_subscription')}
      error={error}
      onClose={() => setConfirmCancelSubscription(false)}
      buttons={[
        {
          title: { key: 'subscription:subscription_title_cancel_subscription' },
          onClick: () => {
            dispatch(cancelSubscription())
            setConfirmCancelSubscription(false)
          },
          style: 'cancel'
        },
        {
          title: { key: 'subscription:subscription_title_keep_subscription' },
          onClick: () => {
            setConfirmCancelSubscription(false)
          },
          style: 'default'
        }
      ]}
    />

    <div className="flex space-x-2 mb-6 lg:mb-8">
      <div className={`space-y-2 flex-shrink-0`}>
        <Heading2>{isLoading ? <Skeleton className="!w-36" /> : t('subscription:subscription_title_fitgenie_premium')}</Heading2>
        <Paragraph className="text-fg-gray-dark">{isLoading ? <Skeleton className="!w-1/2" /> : subtitleText}</Paragraph>
      </div>

      <div className="flex-grow" />

      <div className={`flex-shrink-0 ${userSubscription?.status === 'active' ? 'hidden' : ''}`}>
        <Button
          buttonStyle="secondary"
          size="sm"
          isSkeletonLoading={isLoading}
          onClick={() => {
            router.push('/upgrade')
          }}
        >
          Learn More
        </Button>
      </div>
    </div>

    <div className={`${userSubscription?.status === 'active' ? 'hidden' : ''} space-y-3 w-full`}>
      {subscriptions.map((subscription) => {
        return <div className="cursor-pointer" key={subscription.id} onClick={() => setSelectedSubscription(subscription)}>
          <AccountSubscriptionOptionView
            subscription={subscription}
            availableSubscriptions={subscriptions}
            isSelected={selectedSubscription?.id === subscription.id}
            isLoading={isLoading} />
        </div>
      })}

      <div className="hidden sm:flex">
        <Button
          size="lg"
          className="flex-shrink-0"
          isLoading={isCreatingCheckout}
          isSkeletonLoading={isLoading}
          onClick={() => {
            const id = selectedSubscription?.id
            id && dispatch(createCheckoutSession({ id }))
          }}
        >
          {t('subscription:subscription_title_go_premium')}
        </Button>

        <div className="flex-grow" />
      </div>

      <div className="sm:hidden">
        <Button
          size="lg"
          isFullWidth
          isLoading={isCreatingCheckout}
          isSkeletonLoading={isLoading}
          onClick={() => {
            const id = selectedSubscription?.id
            id && dispatch(createCheckoutSession({ id }))
          }}
        >
          {t('subscription:subscription_title_go_premium')}
        </Button>
      </div>
    </div>

    <div className={`${userSubscription?.status === 'active' ? '' : 'hidden'} mt-3`}>
      <div className="hidden sm:flex">
        <Button
          size="lg"
          buttonStyle="secondary"
          className="flex-shrink-0"
          isLoading={isCancelingSubscription}
          isSkeletonLoading={isLoading}
          onClick={() => {
            setConfirmCancelSubscription(true)
          }}
        >
          {t('subscription:subscription_title_cancel_subscription')}
        </Button>

        <div className="flex-grow" />
      </div>

      <div className="sm:hidden">
        <Button
          size="lg"
          buttonStyle="secondary"
          isFullWidth
          isLoading={isCancelingSubscription}
          isSkeletonLoading={isLoading}
          onClick={() => {
            setConfirmCancelSubscription(true)
          }}
        >
          {t('subscription:subscription_title_cancel_subscription')}
        </Button>
      </div>
    </div>
  </div>
}